import { useEffect, useState } from "react";
import { Col } from "src/components/layout";
import FormLayout from "src/layouts/FormLayout";
import MailingAddressModal from "./MailingAddressModal";
import PreviousAddressModal from "./PreviousAddressModal";
import { useRedirectToPage, useSectionProgress } from "src/hooks";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import { useAuth } from "src/context";
import { AddressOptions } from "src/constants";
import type { DomainAddressType, DtoAddress } from "@porchpass/api-client";
import {
  type AddressesListFormData,
  type AddressListItemFormData,
  type CurrentAddressFormData,
  type MailingAddressFormData,
  type PreviousAddressFormData,
  fromAddressesList,
} from "src/api/adapters/address";
import AddressItem from "./AddressItem";
import CurrentAddressModal from "./CurrentAddressModal";
import { ButtonPopup, ButtonPopupItem } from "src/components/ButtonPopup";
import {
  Add01Icon,
  Alert,
  FormTable,
  FormTableBody,
  Location03Icon,
} from "@porchpass/ui";

export default () => {
  const [focusedAddress, setFocusedAddress] = useState<
    AddressListItemFormData | { address_type: DomainAddressType } | undefined
  >(undefined);
  const [dataLoaded, setDataLoaded] = useState(false);
  const sectionProgress = useSectionProgress();
  const { user: firebaseUser } = useAuth();
  const api = useWebClientApi();
  const { formData, loading, reloadFormData } =
    useFormLoad<AddressesListFormData>(
      {
        addresses: [],
        has_sufficient_address_history: false,
      },
      () => api.getAddresses(),
      (data) =>
        fromAddressesList(data as Parameters<typeof fromAddressesList>[0]),
      { enabled: !!firebaseUser },
    );
  const closeHandler = () => {
    setFocusedAddress(undefined);
  };

  const confirmHandler = () => {
    setFocusedAddress(undefined);
    reloadFormData();
  };

  const hasCurrentAddress = formData.addresses.find(
    (x) => x.address_type == "CURRENT",
  );
  const hasMailingAddress = formData.addresses.find(
    (x) => x.address_type === "MAILING",
  );

  const availableButtonPopupItems = AddressOptions.filter(
    (address) =>
      !(
        (address.address_type === "CURRENT" && hasCurrentAddress) ||
        (address.address_type === "MAILING" && hasMailingAddress)
      ),
  );

  useEffect(() => {
    if (loading || !formData || formData.addresses === undefined) return;

    if (!dataLoaded) {
      setDataLoaded(true);
    }

    let newFocusedAddress:
      | AddressListItemFormData
      | { address_type: DomainAddressType }
      | undefined = undefined;

    if (formData.addresses.length === 0) {
      newFocusedAddress = { address_type: "CURRENT" };
    } else if (!hasCurrentAddress) {
      newFocusedAddress = { address_type: "CURRENT" };
    } else if (!hasMailingAddress) {
      newFocusedAddress = { address_type: "MAILING" };
    }

    if (
      dataLoaded &&
      newFocusedAddress?.address_type !== focusedAddress?.address_type
    ) {
      setFocusedAddress(newFocusedAddress);
    }
  }, [loading, formData.addresses, dataLoaded]);

  let footerTitle = "";
  if (formData.addresses.length > 0) footerTitle = "Next";
  if (sectionProgress["address"] && sectionProgress["employment"])
    footerTitle = "Save";
  let nextPagePath = "employment";
  if (sectionProgress["address"] && sectionProgress["employment"])
    nextPagePath = "../../progress";
  const navigateToNextPage = useRedirectToPage(nextPagePath);

  const handleNavigateToNextPage = () => {
    if (hasCurrentAddress && hasMailingAddress) {
      navigateToNextPage();
    }
  };

  const handleAddingAddresses = (addressType: DomainAddressType) => {
    setFocusedAddress({ address_type: addressType });
  };

  return (
    <FormLayout
      title="Address Information"
      headerTitle="Address"
      stepIndex={2}
      footerTitle={footerTitle}
      clickHandler={handleNavigateToNextPage}
      isLoading={loading}
    >
      <Col>
        <Alert variant="info">
          We recommend providing at least 3 years of residential history to
          improve your chances.
        </Alert>

        <FormTable
          title="Addresses"
          headerIcon={
            <Location03Icon
              variant="bulk-rounded"
              size={16}
              className="text-[#6F41E6]"
            />
          }
        >
          {availableButtonPopupItems.length > 0 && (
            <FormTableBody className="!p-4">
              <ButtonPopup
                icon={<Add01Icon variant="stroke-rounded" size={16} />}
                text="Add Address"
              >
                {availableButtonPopupItems.map((address, index) => (
                  <ButtonPopupItem
                    key={index}
                    title={address.title}
                    clickHandler={() =>
                      handleAddingAddresses(address.address_type)
                    }
                  />
                ))}
              </ButtonPopup>
            </FormTableBody>
          )}
          {formData.addresses &&
            formData.addresses.map((address, index) => (
              <AddressItem
                key={address.id ?? index}
                index={index}
                address={address as DtoAddress}
                editHandler={() => setFocusedAddress(address)}
                closeHandler={closeHandler}
                deleteHandler={confirmHandler}
              />
            ))}
        </FormTable>
      </Col>
      {focusedAddress && focusedAddress.address_type === "CURRENT" && (
        <CurrentAddressModal
          address={focusedAddress as CurrentAddressFormData & { id?: string }}
          closeHandler={closeHandler}
          confirmHandler={confirmHandler}
          hasMailingAddress={!!hasMailingAddress}
        />
      )}
      {focusedAddress && focusedAddress.address_type === "MAILING" && (
        <MailingAddressModal
          address={focusedAddress as MailingAddressFormData & { id?: string }}
          closeHandler={closeHandler}
          confirmHandler={confirmHandler}
        />
      )}
      {focusedAddress && focusedAddress.address_type === "PREVIOUS" && (
        <PreviousAddressModal
          address={focusedAddress as PreviousAddressFormData & { id?: string }}
          closeHandler={closeHandler}
          confirmHandler={confirmHandler}
        />
      )}
    </FormLayout>
  );
};
